<script setup lang="ts">
import { VSheet } from 'vuetify/components';
import { computed, ref, toRefs, watch } from 'vue';
import { VDialog } from 'vuetify/components/VDialog';
import { useTheme } from 'vuetify';

const emit = defineEmits(['close']);

type Props = {
  expanded: boolean;
  color?: string;
  height?: number;
  extraClasses?: string[];
};
const props = withDefaults(defineProps<Props>(), {
  extraClasses: () => ['px-4', 'py-6'],
});

const drawerContent = ref<InstanceType<typeof VSheet> | null>(null);

const isScrolledToTop = () => {
  if (!drawerContent.value) return false;
  return drawerContent.value.$el?.scrollTop === 0;
};
const startY = ref<number | undefined>(0);
const endY = ref(0);
const swipeDistance = ref(0);

watch(
  () => props.expanded,
  (exp) => {
    if (exp) {
      document.body.style.overscrollBehavior = 'none';
      document.documentElement.style.overscrollBehavior = 'none';
    }
  },
  { immediate: true },
);

const handleClose = () => {
  document.body.style.overscrollBehavior = 'auto';
  document.documentElement.style.overscrollBehavior = 'auto';
  emit('close');
};

const onTouchStart = (event: TouchEvent) => {
  if (isScrolledToTop()) {
    startY.value = event.touches[0].clientY;
  } else {
    startY.value = undefined;
  }
};

const onTouchMove = (event: TouchEvent) => {
  if (!startY.value) return;
  endY.value = event.touches[0].clientY;
  swipeDistance.value = endY.value - startY.value > 0 ? endY.value - startY.value : 0;
  if (swipeDistance.value > 50 && isScrolledToTop()) {
    // Prevent content behind drawer from scrolling but allow drawer content to scroll
    event.preventDefault();
  }
};

const onTouchEnd = () => {
  if (!startY.value) {
    endY.value = 0;
    return;
  }
  const swipeThreshold = 50;
  if (swipeDistance.value > swipeThreshold) {
    endY.value = 0;
    handleClose();
  }
  setTimeout(() => {
    swipeDistance.value = 0;
  }, 300);
};

const drawerContainerStyles = computed(() => {
  const baseDistance = props.height ? `calc(100dvh - ${props.height}px)` : '100px';
  const height = `calc(100dvh - ${baseDistance} - ${swipeDistance.value}px) !important`;
  const top = `calc(${baseDistance} + ${swipeDistance.value}px)`;
  return {
    minHeight: height,
    maxHeight: height,
    top,
  };
});
const currentTheme = computed(() => useTheme().global.name.value);

const { expanded } = toRefs(props);
const localExpanded = ref(expanded.value);

watch(expanded, (val) => {
  localExpanded.value = val;
});

watch(localExpanded, (val) => {
  if (!val && expanded.value) {
    // Vuetify tried to close, but parent still open — blocking
    localExpanded.value = true;
    return;
  }
});
</script>

<template>
  <v-dialog transition="dialog-bottom-transition" fullscreen v-model="localExpanded" @click="handleClose">
    <v-sheet
      :theme="currentTheme"
      ref="drawerContent"
      position="relative"
      :color="color || 'surface-container-lowest'"
      width="100vw"
      :class="[{ expanded }, extraClasses]"
      class="drawer-container d-flex flex-column gr-4"
      :style="drawerContainerStyles"
      @touchstart.stop="onTouchStart"
      @touchmove="onTouchMove"
      @touchend.stop="onTouchEnd"
      @click.stop
    >
      <v-btn size="48" variant="plain" class="d-flex close-btn text-none" @click="handleClose">
        <slot name="close">
          <v-icon size="24" icon="mdi-close"></v-icon>
        </slot>
      </v-btn>
      <slot></slot>
    </v-sheet>
  </v-dialog>
</template>

<style scoped lang="scss">
.drawer-container {
  overscroll-behavior: none;
  border-top-right-radius: 12px !important;
  border-top-left-radius: 12px !important;
  overflow-y: scroll;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1008;
}

.close-btn {
  position: absolute;
  right: 12px;
  top: 12px;
  z-index: 1;
  aspect-ratio: 1;
  height: unset;
  border-radius: 8px;
}
</style>
